import React, { useState } from 'react';
import { theme } from '../../theme';
import { useVenues } from '../../hooks/venue/useVenues';
import VenueFilterSidebar from '../../components/venue/shared/VenueFilterSidebar';
import VenueCard from '../../components/venue/shared/VenueCard';
import Pagination from '../../components/venue/shared/Pagination';
import { Search, MapPin } from 'lucide-react';

const VenueSearchPage: React.FC = () => {
  const {
    venues,
    filters,
    pagination,
    loading,
    error,
    updateFilters,
    resetFilters,
    goToPage,
  } = useVenues();

  const [cityInput, setCityInput] = useState(filters.city || '');

  const handleCitySearch = () => {
    if (cityInput.trim()) {
      updateFilters({ city: cityInput.trim() });
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleCitySearch();
    }
  };

  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: theme.colors.background,
    padding: '32px',
  };

  const headerStyle: React.CSSProperties = {
    marginBottom: '32px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2.5rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '12px',
    fontFamily: theme.fonts.heading,
    letterSpacing: theme.fonts.letterSpacingWide,
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    color: theme.colors.textSecondary,
    marginBottom: '24px',
  };

  const searchBarContainerStyle: React.CSSProperties = {
    display: 'flex',
    gap: '12px',
    marginBottom: '32px',
    maxWidth: '600px',
  };

  const searchInputContainerStyle: React.CSSProperties = {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  };

  const searchIconStyle: React.CSSProperties = {
    position: 'absolute',
    left: '16px',
    color: theme.colors.textSecondary,
  };

  const searchInputStyle: React.CSSProperties = {
    flex: 1,
    padding: '14px 16px 14px 48px',
    backgroundColor: theme.colors.surface,
    color: theme.colors.text,
    border: `2px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    fontSize: theme.fonts.sizeBody,
  };

  const searchButtonStyle: React.CSSProperties = {
    padding: '14px 32px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    border: 'none',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: 'pointer',
    transition: 'all 0.2s',
  };

  const mainContentStyle: React.CSSProperties = {
    display: 'flex',
    gap: '32px',
    alignItems: 'flex-start',
  };

  const resultsContainerStyle: React.CSSProperties = {
    flex: 1,
  };

  const resultsHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
  };

  const resultsCountStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '24px',
    marginBottom: '32px',
  };

  const emptyStateStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
  };

  const emptyIconStyle: React.CSSProperties = {
    marginBottom: '16px',
    color: theme.colors.textSecondary,
  };

  const emptyTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: '8px',
  };

  const emptyMessageStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
  };

  const loadingStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '64px 32px',
    fontSize: theme.fonts.sizeSubtitle,
    color: theme.colors.textSecondary,
  };

  const errorStyle: React.CSSProperties = {
    textAlign: 'center',
    padding: '32px',
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    border: `2px solid ${theme.colors.danger}`,
  };

  const errorTitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSubtitle,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.danger,
    marginBottom: '8px',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>BUSCAR CANCHAS</h1>
        <p style={subtitleStyle}>
          Encuentra la cancha perfecta para tu próximo partido
        </p>

        {/* Search Bar */}
        <div style={searchBarContainerStyle}>
          <div style={searchInputContainerStyle}>
            <MapPin size={20} style={searchIconStyle} />
            <input
              type="text"
              placeholder="Buscar por ciudad..."
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              onKeyPress={handleKeyPress}
              style={searchInputStyle}
            />
          </div>
          <button
            style={searchButtonStyle}
            onClick={handleCitySearch}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#00D96F';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = theme.colors.primary;
            }}
          >
            Buscar
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Filter Sidebar */}
        <VenueFilterSidebar
          filters={filters}
          onFiltersChange={updateFilters}
          onReset={resetFilters}
        />

        {/* Results */}
        <div style={resultsContainerStyle}>
          {/* Results Header */}
          <div style={resultsHeaderStyle}>
            <div style={resultsCountStyle}>
              {pagination.total > 0 ? (
                <>
                  Mostrando{' '}
                  <strong>
                    {(pagination.page - 1) * pagination.limit + 1}-
                    {Math.min(pagination.page * pagination.limit, pagination.total)}
                  </strong>{' '}
                  de <strong>{pagination.total}</strong> canchas
                </>
              ) : (
                'No hay resultados'
              )}
            </div>
          </div>

          {/* Loading State */}
          {loading && <div style={loadingStyle}>Cargando canchas...</div>}

          {/* Error State */}
          {error && !loading && (
            <div style={errorStyle}>
              <div style={errorTitleStyle}>Error al cargar canchas</div>
              <div>{error}</div>
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && venues.length === 0 && (
            <div style={emptyStateStyle}>
              <div style={emptyIconStyle}>
                <Search size={64} />
              </div>
              <div style={emptyTitleStyle}>No se encontraron canchas</div>
              <div style={emptyMessageStyle}>
                Intenta ajustar los filtros de búsqueda o buscar en otra ciudad
              </div>
            </div>
          )}

          {/* Results Grid */}
          {!loading && !error && venues.length > 0 && (
            <>
              <div style={gridStyle}>
                {venues.map((venue) => (
                  <VenueCard key={venue.id} venue={venue} />
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.totalPages}
                onPageChange={goToPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VenueSearchPage;
